import React from 'react';
import moment from 'moment-timezone';
import { flatMap } from 'lodash';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Columns2 from 'components/Columns2';
import Container from 'components/Container';
import { CoachingSessionFrequency } from 'coaching/CoachingConstants';
import GetFullCoachScheduleContainer, {
  CoachUpcomingSession,
} from 'coaching/containers/GetFullCoachScheduleContainer';
import CoachAvailabilitiesByDayList from 'coaching/components/CoachAvailabilityView/CoachAvailabilitiesByDayList';
import MediaObject from 'components/MediaObject';
import Notice from 'components/Notice';
import Row from 'components/Row';
import Text from 'components/Text';
import { CoachingEventDefinitionInput } from 'globalTypes';
import { formatDateForCoordinator, formatTimeForCoordinator } from 'lib/dateTimeFormat';
import useFullWeekOffset from 'coaching/hooks/useFullWeekOffset';

export type Props = {
  coachUuid?: string;
  coachName: string;
  proposedEventTimeframe: CoachingEventDefinitionInput;
  unavailableDates?: string[];
  coachIsFullyAvailable?: boolean;
  eventFrequency: CoachingSessionFrequency;
  loading: boolean;
};

export default function CoachingCoordinatorAvailabilityConflicts(props: Props) {
  if (props.loading) {
    return <CenteredLoadingIndicator />;
  }

  const { unavailableDates, coachUuid, proposedEventTimeframe } = props;

  if (props.coachIsFullyAvailable === undefined || unavailableDates === undefined) {
    return null;
  }

  // all dates/times in the coordinator tool are set to PT
  const timezone = 'America/Los_Angeles';
  const { startDate, endDate, advanceWeekOffset, regressWeekOffset, state } = useFullWeekOffset(
    timezone
  );

  const showDates = unavailableDates?.length > 0 && props.eventFrequency !== 'once';
  const unavailabilityText = showDates ? (
    <Text weight="semibold">
      {unavailableDates.map(date => formatDateForCoordinator(date, 'MMM D')).join(', ')}.
    </Text>
  ) : (
    'at the selected timeslot.'
  );

  return props.coachIsFullyAvailable ? null : (
    <>
      <Row>
        <Notice color="paper" noShadow noBorderRadius data-test="availability-conflict-notice">
          {props.coachName} is unavailable {unavailabilityText}
        </Notice>
      </Row>
      <GetFullCoachScheduleContainer
        coachUuid={coachUuid}
        startDate={startDate}
        endDate={endDate}
        timezone={timezone}>
        {({ upcomingSessions, availabilities }) => {
          return (
            <Container size="large" align="left" flush>
              <Columns2 gutter="medium">
                <CoachAvailabilitiesByDayList
                  availabilities={availabilities}
                  onWeekBack={regressWeekOffset}
                  onWeekForward={advanceWeekOffset}
                  weekOffset={state.weekOffset}
                  weekStartDate={startDate}
                  weekEndDate={endDate}
                  timezone={state.timezone}
                  editable={false}
                />
                {renderUpcomingSessions(upcomingSessions, proposedEventTimeframe)}
              </Columns2>
            </Container>
          );
        }}
      </GetFullCoachScheduleContainer>
    </>
  );
}

function renderHeading(text: string) {
  return (
    <Row size="xsmall">
      <Text color="graphite" weight="semibold">
        {text}
      </Text>
    </Row>
  );
}

function renderSingleDayRow(originalStartDateTime: moment.Moment, endDateTime?: moment.Moment) {
  return (
    <Row size="xsmall" key={originalStartDateTime.toISOString()} data-test="single-day-row">
      <MediaObject>
        <Text weight="semibold">
          {formatDateForCoordinator(originalStartDateTime, 'ddd, MMM D')}
        </Text>
        <Text>
          {formatTimeForCoordinator(originalStartDateTime)}
          {endDateTime && ` — ${formatTimeForCoordinator(endDateTime)}`}
        </Text>
      </MediaObject>
    </Row>
  );
}

function renderUpcomingSessions(
  sessions: CoachUpcomingSession[],
  proposedEventTimeframe: CoachingEventDefinitionInput
) {
  const sessionDatesForTheNextWeek = flatMap(sessions, session =>
    session.instances.map(instance => instance.startDateTime)
  )
    .filter(dateTime =>
      moment(dateTime).isBefore(moment(proposedEventTimeframe.startDateTime).add(7, 'days'))
    )
    .sort((a, b) => (moment(a).isBefore(b) ? -1 : 1));
  return (
    <>
      {renderHeading('Upcoming sessions')}
      {sessionDatesForTheNextWeek.map(sessionDate => renderSingleDayRow(moment(sessionDate)))}
    </>
  );
}
